import React from 'react';
import { Feather } from '@expo/vector-icons';
import { Pressable, Text, View, StyleSheet } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Paprika } from '../../../../styles/theme';
import { ENTRY_META, EntryIcon } from '../entryMeta';
import { BottomSheet } from '../primitives/BottomSheet';

type MenuKey = 'recipe' | 'dining' | 'leftover' | 'note' | 'skip' | 'clear';

interface MenuItem {
  key: MenuKey;
  labelKey: string;
  subKey?: string;
  tint: string;
  color: string;
  entryType?: 'dining' | 'leftover' | 'note' | 'skip';
}

const MENU_ITEMS: MenuItem[] = [
  { key: 'recipe',   labelKey: 'entry.chooseRecipe',   subKey: 'entry.subLibrary',    tint: Paprika.brandTint,        color: Paprika.primary },
  { key: 'dining',   labelKey: 'entry.diningLong',     subKey: 'entry.subEatingOut',  tint: ENTRY_META.dining.tint,   color: ENTRY_META.dining.color,   entryType: 'dining' },
  { key: 'leftover', labelKey: 'entry.leftovers',      subKey: 'entry.subRecentMeal', tint: ENTRY_META.leftover.tint, color: ENTRY_META.leftover.color, entryType: 'leftover' },
  { key: 'note',     labelKey: 'entry.noteFree',       subKey: 'entry.subNoteHint',   tint: ENTRY_META.note.tint,     color: ENTRY_META.note.color,     entryType: 'note' },
  { key: 'skip',     labelKey: 'entry.nothingThatDay', subKey: 'entry.subNoPlan',     tint: ENTRY_META.skip.tint,     color: ENTRY_META.skip.color,     entryType: 'skip' },
];

interface Props {
  visible: boolean;
  hasContent: boolean;
  dayLabel: string;
  onPick: (key: MenuKey) => void;
  onClose: () => void;
}

export function ChooserSheet({ visible, hasContent, dayLabel, onPick, onClose }: Props) {
  const { t } = useTranslation();
  return (
    <BottomSheet visible={visible} onClose={onClose} maxHeight="80%">
      {/* Header */}
      <View style={styles.header}>
        <Text style={styles.title}>
          {hasContent ? t('entry.editMeal') : t('entry.addMeal')}
        </Text>
        <Text style={styles.subtitle}>{dayLabel}</Text>
      </View>

      {/* Menu items */}
      {MENU_ITEMS.map((item) => (
        <Pressable
          key={item.key}
          style={styles.item}
          onPress={() => onPick(item.key)}
          testID={`chooser-${item.key}`}
        >
          <View style={[styles.iconBox, { backgroundColor: item.tint }]}>
            {item.entryType ? (
              <EntryIcon type={item.entryType} size={16} color={item.color} />
            ) : (
              <Feather name="book-open" size={16} color={item.color} />
            )}
          </View>
          <View style={styles.itemText}>
            <Text style={styles.itemLabel}>{t(item.labelKey)}</Text>
            {!!item.subKey && <Text style={styles.itemSub}>{t(item.subKey)}</Text>}
          </View>
          <Feather name="chevron-right" size={18} color={Paprika.textMuted} />
        </Pressable>
      ))}

      {/* Clear button (if hasContent) */}
      {hasContent && (
        <View style={styles.clearButtonContainer}>
          <Pressable
            style={styles.clearButton}
            onPress={() => onPick('clear')}
          >
            <Text style={styles.clearButtonText}>{t('thisWeek.clearSlot')}</Text>
          </Pressable>
        </View>
      )}
    </BottomSheet>
  );
}

const styles = StyleSheet.create({
  header: {
    gap: 4,
    paddingHorizontal: 16,
    paddingTop: 4,
    paddingBottom: 12,
  },
  title: {
    fontFamily: 'Newsreader-Regular',
    fontSize: 21,
    lineHeight: 28,
    color: Paprika.textPrimary,
  },
  subtitle: {
    fontFamily: 'Inter-Regular',
    fontSize: 13,
    color: Paprika.textSecondary,
  },
  item: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
    minHeight: 48,
  },
  iconBox: {
    width: 42,
    height: 42,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  itemText: {
    flex: 1,
    gap: 2,
  },
  itemLabel: {
    fontFamily: 'Inter-Medium',
    fontSize: 14,
    fontWeight: '500' as const,
    color: Paprika.textPrimary,
  },
  itemSub: {
    fontFamily: 'Inter-Regular',
    fontSize: 12,
    color: Paprika.textSecondary,
  },
  clearButtonContainer: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    marginTop: 8,
  },
  clearButton: {
    backgroundColor: Paprika.bgSunk,
    borderRadius: 100,
    paddingVertical: 12,
    paddingHorizontal: 16,
    alignItems: 'center',
  },
  clearButtonText: {
    fontFamily: 'Inter-Medium',
    fontSize: 14,
    fontWeight: '500' as const,
    color: Paprika.textSecondary,
  },
});
